import z from "zod";

export type Integration = {
  uniqueName: string;
  name: string;
  signature: string;
  website: string;
  iconSVG: string;
  category: "CRM" | "Dialer" | "Storage" | "Other";
  description?: string;
};

export type ProviderCapabilities = {
  externalCredentials: boolean;
  retrieveFields: boolean;
  retrieveStatus: boolean;
  searchEntities: boolean;
  registration: boolean;
  orgMetadata: boolean;
  editEntities: boolean;
  retrieveDelta: boolean;
  exportEntities: boolean;
  generateEvents: boolean;
  receiveEvents: boolean;
  thirdPartyExport: boolean;
  updateableThirdParty: boolean;
  synchronizableThirdParty: boolean;
  directLinkProvider: boolean;
  websiteContactImport: boolean;
  storage: boolean;
};

export type AvailableProvider = {
  capabilities: ProviderCapabilities;
  name: string;
  signature: string;
  uniqueName: string;
};

export type LassoAuthenticationResponse = {
  scheme: AuthenticationScheme;
  contentProviderUserId: string;
  oAuthRedirectUrl: string;
  authToken: string;
};

export enum AuthenticationScheme {
  OAuth = "OAuth",
  Credentials = "Credentials",
  AccessKey = "AccessKey",
  CredentialsAndAccessKey = "CredentialsAndAccessKey",
}

export type IntegrationConnectionData = Record<string, unknown>;

export const providerConnectionInfoFieldSchema = z.object({
  description: z.string().nullable(),
  mappingGroupName: z.string().nullable(),
  mappingGroupId: z.string().nullable(),
  mappingEntityTypes: z.array(z.string()),
  entity: z.string(),
  entityName: z.string().nullable(),
  fieldId: z.string(),
  fieldName: z.string().nullable(),
  type: z.string().nullable(),
  sourceType: z.string().nullable(),
  required: z.boolean(),
  updatable: z.boolean(),
  transformers: z.array(z.string()).nullable(),
  visible: z.boolean(),
});
export type ProviderConnectionInfoField = z.infer<
  typeof providerConnectionInfoFieldSchema
>;

export enum ProviderAuthenticationType {
  None = "None",
  UserName = "UserName",
  UsernameAndPassword = "UsernameAndPassword",
  AccessKey = "AccessKey",
  AccessKeyAndURL = "AccessKeyAndURL",
  UsernameAndPasswordAndURL = "UsernameAndPasswordAndURL",
  Certificate = "Certificate",
  CertificateAndPassword = "CertificateAndPassword",
  CertificateAndURL = "CertificateAndURL",
  CertificateAndPasswordAndURL = "CertificateAndPasswordAndURL",
  UsernameAndPasswordAndAccessKey = "UsernameAndPasswordAndAccessKey",
  OAuth = "OAuth",
  API = "API",
  OAuthAndURL = "OAuthAndURL",
  OAuthClientCredentialsAndURL = "OAuthClientCredentialsAndURL",
}
export const ProviderAuthenticationTypeLabels: Record<
  ProviderAuthenticationType,
  string
> = {
  [ProviderAuthenticationType.None]: "Ingen godkendelse",
  [ProviderAuthenticationType.UserName]: "Brugernavn",
  [ProviderAuthenticationType.UsernameAndPassword]: "Brugernavn og adgangskode",
  [ProviderAuthenticationType.AccessKey]: "Adgangsnøgle",
  [ProviderAuthenticationType.AccessKeyAndURL]: "Adgangsnøgle og URL",
  [ProviderAuthenticationType.UsernameAndPasswordAndURL]:
    "Brugernavn, adgangskode og URL",
  [ProviderAuthenticationType.Certificate]: "Certifikat",
  [ProviderAuthenticationType.CertificateAndPassword]:
    "Certifikat og adgangskode",
  [ProviderAuthenticationType.CertificateAndURL]: "Certifikat og URL",
  [ProviderAuthenticationType.CertificateAndPasswordAndURL]:
    "Certifikat, adgangskode og URL",
  [ProviderAuthenticationType.UsernameAndPasswordAndAccessKey]:
    "Brugernavn, adgangskode og adgangsnøgle",
  [ProviderAuthenticationType.OAuth]: "OAuth",
  [ProviderAuthenticationType.API]: "API",
  [ProviderAuthenticationType.OAuthAndURL]: "OAuth",
  [ProviderAuthenticationType.OAuthClientCredentialsAndURL]:
    "OAuth client credentials",
};

export type ProviderConfigurationMetadata = {
  providerTitle: string | null;
  authenticationType: ProviderAuthenticationType;
  passwordTitle: string | null;
  passwordDescription: string | null;
  usernameTitle: string | null;
  usernameDescription: string | null;
  apiUrlTitle: string | null;
  apiUrlDescription: string | null;
  accessKeyTitle: string | null;
  accessKeyDescription: string | null;
  certificateTitle: string | null;
  certificateDescription: string | null;
  hasMappings: boolean;
  requiresMappings: boolean;
  introText: string | null;
};

export type ProviderConnectionInfoSettings = {
  exportMappings: ProviderConnectionInfoFieldMapping[];
  other?: { [key: string]: string };
  notificationSettings?: Record<ProviderErrorEnum, NotificationSettings>;
};

export type NotificationSettings = {
  emails: string[];
  userIds: string[];
  sendToAdmins: boolean;
};

export enum ProviderErrorEnum {
  AuthenticationError = "AuthenticationError",
  BatchFailed = "BatchFailed",
  BatchDidNotFinish = "BatchDidNotFinish",
  BatchUnableToStart = "BatchUnableToStart",
  IdNotFound = "IdNotFound",
  UnknownError = "UnknownError",
  Duplicate = "Duplicate",
  RelatedEntityFailed = "RelatedEntityFailed",
  EntityFailed = "EntityFailed",
  InvalidMappings = "InvalidMappings",
  InvalidField = "InvalidField",
  MissingCampaign = "MissingCampaign",
  HostIssues = "HostIssues",
  InvalidHostResponse = "InvalidHostResponse",
  HostDidNotRespondInTime = "HostDidNotRespondInTime",
  EntityTypeNotFound = "EntityTypeNotFound",
  UpdateDisabled = "UpdateDisabled",
  MissingPrivileges = "MissingPrivileges",
  Throttled = "Throttled",
  EntityNotAvailable = "EntityNotAvailable",
  MissingPhoneNumber = "MissingPhoneNumber",
  InvalidData = "InvalidData",
  EntityLimit = "EntityLimit",
}

export type ProviderErrorDescription = {
  title: string;
  description: string;
};

export type ProviderConnectionInfoFieldMapping = {
  update: boolean;
  from: ProviderConnectionInfoField;
  to: ProviderConnectionInfoField;
  locked: boolean;
};

export type ProviderConnectionInfo = {
  encrypted: boolean;
  provider: string;
  providerName: string;
  apiUrl: string;
  userName: string;
  password: string;
  accessKey: string;
  contentProviderUserId: string;
  authType: ProviderAuthenticationType | null;
  settings: ProviderConnectionInfoSettings | null;
};

export interface SettingsSection {
  title: string;
  description: string;
  order: number;
  fields: SettingsInputField[];
}

export interface SettingsInputField {
  name: string;
  description: string;
  id: string;
  type: SettingsInputType;
  mandatory: boolean;
  showIfTrue: string;
  order: number;
  options: SettingsInputOptions[];
  defaultValue: string;
  validationType: string;
}

export enum SettingsInputType {
  Text = "Text",
  Dropdown = "Dropdown",
  Checkbox = "Checkbox",
  Multiselect = "Multiselect",
  AdvancedDatetime = "AdvancedDatetime",
}

export interface SettingsInputOptions {
  value: string;
  id: string;
}

export type Inputs = {
  steps: {
    stepNumber: number;
    title: string;
    description: string;
    inputs: ExportInput[];
  }[];
};

export type ExportInput = {
  name: string;
  description: string;
  id: string;
  type: string;
  mandatory: boolean;
  showIfTrue: string | null;
  requiredIfTrue: string | null;
  adminFeature: boolean;
  order: number;
  advanced: boolean;
  remember: boolean;
  options: ExportInputOption[];
  defaultValue: string;
  validationType: string | null;
  visible: boolean;
};

export type ExportInputOption = {
  value: string;
  id: string;
};

export interface ThirdPartyImportResult {
  id: string;
  success: boolean;
  error: ProviderError;
  additionalInformation: string;
  relatedResults: ThirdPartyImportResult[];
  objectType: string;
}

export interface ProviderError {
  type: ProviderErrorEnum;
  message: string;
  logError: boolean;
  canBeRetried: boolean;
  isWarning: boolean;
}

export const campaignIntegrationFormSchema = z.object({
  exportSystem: z.string().nullable(),
  exportMode: z.enum(["SalesAutomatic", "SalesManual"]).nullable(),
  exportInput: z.record(z.string(), z.string()).optional(),
  deliverContactsRequirePhone: z.boolean().optional(),
  deliverContacts: z.boolean(),
  deliverLiveNumber: z.boolean().optional(),
});

export type CampaignIntegrationFormValues = z.infer<
  typeof campaignIntegrationFormSchema
>;

// export type IntegrationField = {
//   name: string;
//   displayName: string;
//   type: "text" | "password" | "url" | "select" | "checkbox";
//   required: boolean;
//   description?: string;
//   placeholder?: string;
//   options?: { value: string; label: string }[];
//   validation?: {
//     pattern?: string;
//     minLength?: number;
//     maxLength?: number;
//   };
// };
